import { KeyboardEvent, useCallback } from "react";

import { Input } from "@design-system/Input/Input";
import { InputBox } from "@design-system/InputBox/InputBox";
import { Select } from "@design-system/Select/Select";

export type DomainChip = {
  id: string;
  domain: string;
};

type Props = {
  domains: DomainChip[];
  draft: string;
  onDraftChange: (draft: string) => void;
  onAdd: (domain: string) => void;
  onRemove: (id: string) => void;
  invalidIds?: string[];
  invalidMessages?: Record<string, string>;
  disabled?: boolean;
  placeholder?: string;
};

const isCommitKey = (key: string) => key === "Enter" || key === " " || key === ",";

export const DomainChipInput = ({
  domains,
  draft,
  onDraftChange,
  onAdd,
  onRemove,
  invalidIds = [],
  invalidMessages = {},
  disabled,
  placeholder = "Type a domain and press Enter, Space, or comma",
}: Props) => {
  const options = domains.map((domain) => ({
    id: domain.id,
    label: domain.domain,
  }));

  const commitValue = useCallback(
    (raw: string) => {
      const value = raw.trim().replace(/,+$/, "");
      if (!value || disabled) {
        return;
      }

      const alreadyListed = domains.some(
        (domain) => domain.domain.toLowerCase() === value.toLowerCase(),
      );
      if (alreadyListed) {
        return;
      }

      onAdd(value);
    },
    [disabled, domains, onAdd],
  );

  const commitDraft = useCallback(() => {
    commitValue(draft);
    onDraftChange("");
  }, [commitValue, draft, onDraftChange]);

  const handleChange = (value: string) => {
    if (disabled) {
      return;
    }

    if (/[,\s]/.test(value)) {
      const parts = value.split(/[,\s]+/);
      const endsWithDelimiter = /[,\s]$/.test(value);
      const remainder = endsWithDelimiter ? "" : (parts.pop() ?? "");
      for (const part of parts) {
        commitValue(part);
      }
      onDraftChange(remainder);
      return;
    }

    onDraftChange(value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (isCommitKey(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      commitDraft();
      return;
    }

    if (event.key === "Backspace" && !draft && domains.length > 0) {
      event.preventDefault();
      onRemove(domains[domains.length - 1].id);
    }
  };

  return (
    <InputBox
      wrap
      leftSlot={
        domains.length > 0 ? (
          <>
            {domains.map((domain) => (
              <Select.TagValue
                key={domain.id}
                options={options}
                value={domain.id}
                color={invalidIds.includes(domain.id) ? "danger" : "secondary"}
                title={invalidMessages[domain.id]}
                onRemove={disabled ? undefined : onRemove}
              />
            ))}
          </>
        ) : undefined
      }
    >
      <Input
        value={draft}
        onChange={(event) => handleChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={domains.length === 0 ? placeholder : ""}
        disabled={disabled}
        size={1}
      />
    </InputBox>
  );
};
